import { Button, Col, Input, message, Row, Space, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  PrinterOutlined,
  MailOutlined,
} from "@ant-design/icons";
import {
  displayFormattedDate,
  formatCurrency,
  getClientConfig,
  isEmpty,
} from "../../../generalFunctions";
import useApi from "../../../hooks/useApi";
import EmailModal from "../../common/EmailModal";
import PrintPdfModal from "../../common/PrintPdfModal";
import TableComponent from "../../common/table/TableComponent";
import AddReleaseOrder from "./AddReleaseOrder";
import { CheckCircleOutlined } from "@ant-design/icons";
import DeleteModal from "../../common/DeleteModal";

function ReleaseOrderTable(props) {
  const columns = [
    {
      title: "RO NO.",
      dataIndex: "id",
      width: "5%",
    },
    {
      title: "RO Date",
      dataIndex: "roDate",
      render: (text) => displayFormattedDate(text),
      width: "10%",
    },
    {
      title: "Publication",
      dataIndex: "publication",
      width: "20%",
    },
    {
      title: "Client",
      dataIndex: "client",
      width: "20%",
    },
    {
      title: "Bill For",
      dataIndex: "billFor",
      width: "20%",
    },
    {
      title: "RO Amount",
      dataIndex: "roAmount",
      width: "10%",
      render: (text) => formatCurrency(text),
    },
    {
      title: "Action",
      key: "action",

      render: (text, record) => (
        <Space>
          <Button size="small" onClick={() => editRecord(record)}>
            <EditOutlined />
          </Button>
          <Button size="small" onClick={() => editRecord(record, true)}>
            <EyeOutlined />
          </Button>
          <Button size="small" onClick={() => handlePrint(record)}>
            <PrinterOutlined />
          </Button>
          <Button size="small" onClick={() => handleDelete(record)}>
            <DeleteOutlined />
          </Button>
          <Button size="small" onClick={() => handleEmail(record)}>
            <MailOutlined />
          </Button>
          {!isEmpty(record.isEmailSent) && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {record.isEmailSent === 1 ? "Sent" : ""}
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  const [responseData, getRequest] = useApi(
    "/releaseOrder",
    null,
    API_METHODS.GET
  );
  const [visible, setVisible] = useState(false);
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [visiblePrint, setVisiblePrint] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [tableData, setTableData] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [singleGetResponse, singleGetRequest] = useApi(
    `/releaseOrder/`,
    null,
    API_METHODS.GET
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleEmail, setVisibleEmail] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const { publication } = getClientConfig();

  useEffect(() => {
    getRequest(
      null,
      API_METHODS.GET,
      `/releaseOrder?pageNumber=${1}&isClassified=${props.isClassified}`
    );
  }, [props.isClassified]);

  useEffect(() => {
    const data = responseData.data !== BLANK_VALUE ? responseData.data : [];
    setTableData(data);
    setTotalRecords(data && data.length > 0 ? data[0].count : 0);
  }, [responseData]);

  const handleAddReleaseOrder = (isUpdated) => {
    const flag = visible ? false : true;
    setVisible(flag);
    if (isUpdated === true) {
      getRequest(
        null,
        API_METHODS.GET,
        `/releaseOrder?pageNumber=${1}&isClassified=${props.isClassified}`
      );
    }
  };

  const handleEditReleaseOrder = (isUpdated) => {
    const flag = visibleEdit ? false : true;
    !flag && setSelectedRecord(null);
    setVisibleEdit(flag);
    if (isUpdated === true) {
      getRequest(
        null,
        API_METHODS.GET,
        `/releaseOrder?pageNumber=${1}&isClassified=${props.isClassified}`
      );
    }
  };

  const editRecord = (record, isViewOnly = false) => {
    setIsViewOnly(isViewOnly);
    setVisibleEdit(true);
    setSelectedRecord(record);
  };

  const handlePrint = (record) => {
    const flag = visiblePrint ? false : true;
    record && setSelectedRecord(record);
    setVisiblePrint(flag);
  };

  const onReleaseOrderSearch = (id) => {
    if (!isEmpty(id))
      singleGetRequest(
        null,
        API_METHODS.GET,
        `/releaseOrder/${id}?isClassified=${props.isClassified}`
      );
  };

  useEffect(() => {
    if (
      singleGetResponse &&
      !isEmpty(singleGetResponse.data) &&
      !singleGetResponse.isLoading
    ) {
      const { data } = singleGetResponse;
      setIsViewOnly(false); // open in edit mode
      setVisibleEdit(true);
      setSelectedRecord(data[0]);
    } else if (
      singleGetResponse &&
      singleGetResponse.data === BLANK_VALUE &&
      !singleGetResponse.isLoading
    ) {
      message.error("Realease Order not found");
    }
  }, [singleGetResponse]);

  const onRowSelectChange = (newSelectedRowKeys, records) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(records);
  };

  const rowSelection = {
    selectedRowKeys,
    type: "checkbox",
    onChange: onRowSelectChange,
  };

  const onPaginationChange = (page) => {
    getRequest(
      null,
      API_METHODS.GET,
      `/releaseOrder?pageNumber=${page.current}&isClassified=${props.isClassified}`
    );
    setCurrentPage(page.current);
  };

  const handleEmail = (record) => {
    const flag = visibleEmail ? false : true;
    record && setSelectedRecord(record);
    setVisibleEmail(flag);
  };

  const handleDelete = (record) => {
    const flag = visibleDelete ? false : true;
    record && setSelectedRecord(record);
    setVisibleDelete(flag);
  };

  const getAccountIdForEmail = (selectedRecord) => {
    const publicationInfo = publication.find(
      (type) => type.id === selectedRecord.publicationId
    );
    if (props.isClassified === 1) {
      return publicationInfo.classifiedCreditorId;
    } else {
      return publicationInfo.creditorId;
    }
  };

  return (
    <>
      <Row>
        <Col span={6}>
          <Typography.Title level={5}>Release Orders</Typography.Title>
        </Col>
        <Col span={18}>
          <Space style={{ marginBottom: 10, float: "right" }}>
            <Input.Search
              prefix="RO-"
              placeholder="Release Order Number"
              allowClear
              enterButton="Search"
              onSearch={onReleaseOrderSearch}
            />
            <Button type="primary" onClick={handleAddReleaseOrder}>
              Release Order
            </Button>
            <Button
              type="primary"
              onClick={handlePrint}
              disabled={selectedRows.length === 0}
            >
              Print Selected RO's
            </Button>
          </Space>
        </Col>
      </Row>
      <TableComponent
        columns={columns}
        tableData={tableData}
        loading={responseData.isLoading}
        rowSelection={rowSelection}
        onPaginationChange={onPaginationChange}
        total={totalRecords}
        current={currentPage}
      />
      {visible && (
        <AddReleaseOrder
          visible={visible}
          close={handleAddReleaseOrder}
          isViewModal={true}
          isClassified={props.isClassified}
        />
      )}
      {visibleEdit && (
        <AddReleaseOrder
          visible={visibleEdit}
          selectedRecord={selectedRecord}
          close={handleEditReleaseOrder}
          isViewOnly={isViewOnly}
          isViewModal={true}
          isClassified={props.isClassified}
        />
      )}
      {visiblePrint && (
        <PrintPdfModal
          visible={visiblePrint}
          data={selectedRows.length !== 0 ? selectedRows : [selectedRecord]}
          title={"Release Order"}
          close={handlePrint}
          pdfType="releaseOrder"
          isClassified={props.isClassified}
        />
      )}
      {visibleEmail && (
        <EmailModal
          attachmentType="ReleaseOrder"
          attachmentName={`RO No. ${selectedRecord.id}`}
          visible={visibleEmail}
          accountId={getAccountIdForEmail(selectedRecord)}
          record={selectedRecord}
          close={handleEmail}
          refresh={() => {
            getRequest(
              null,
              API_METHODS.GET,
              `/releaseOrder?pageNumber=${1}&isClassified=${props.isClassified}`
            );
          }}
          isClassified={props.isClassified}
        />
      )}

      {visibleDelete && (
        <DeleteModal
          visible={visibleDelete}
          close={handleDelete}
          recordType={`ReleaseOrder-${props.isClassified}`}
          record={selectedRecord}
          refresh={() => {
            getRequest(
              null,
              API_METHODS.GET,
              `/releaseOrder?pageNumber=${1}&isClassified=${props.isClassified}`
            );
          }}
        />
      )}
    </>
  );
}

export default ReleaseOrderTable;
